import { BookOpen, Flame, Heart, Link2, Package, Share2, Check, FileText, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { Product } from '../entities/Product'
import { motion } from 'framer-motion'
import { slideInBottom } from '../../../lib/animations'
import { buildProductShareLink } from '../utils/productShare'
import DOMPurify from 'dompurify'

type Props = {
  products: Product[]
  favoriteProductIds: Set<string>
  favoritesLoading?: boolean
  onToggleFavorite: (productId: string) => void
  onSelectProduct?: (productId: string) => void
  onOpenFavorites?: () => void
  onOpenHot?: () => void
  shareReferrerId?: string | null
}

function formatVnd(value: number) {
  return `${Math.round(value).toLocaleString('vi-VN')}đ`
}

function getMinPrice(product: Product) {
  const variants = (product.variants || []).filter(v => !v.is_archived)
  if (!variants.length) return null
  return variants.reduce((min, v) => Math.min(min, Number(v.price || 0)), Number(variants[0].price || 0))
}

function pickNextRandomIndex(length: number, currentIndex: number) {
  if (length <= 1) return 0
  const randomOffset = 1 + Math.floor(Math.random() * (length - 1))
  return (currentIndex + randomOffset) % length
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function buildFallbackDescriptionHtml(product: Product) {
  const sections: string[] = []

  if (product.mechanism?.trim()) {
    sections.push(`<h4>Cơ chế</h4><p>${escapeHtml(product.mechanism.trim())}</p>`)
  }
  if (product.recommended_model?.trim()) {
    sections.push(`<h4>Model khuyến dùng</h4><p>${escapeHtml(product.recommended_model.trim())}</p>`)
  }
  if (product.strengths?.trim()) {
    sections.push(`<h4>Điểm mạnh</h4><p>${escapeHtml(product.strengths.trim())}</p>`)
  }
  if (product.weaknesses?.trim()) {
    sections.push(`<h4>Điểm yếu</h4><p>${escapeHtml(product.weaknesses.trim())}</p>`)
  }

  if (!sections.length) {
    return '<p>Sản phẩm chưa có mô tả chi tiết.</p>'
  }

  return sections.join('')
}

function ProductQuickItem({
  product,
  isFavorite,
  onToggleFavorite,
  onSelect,
  shareReferrerId,
  motionIndex = 0,
}: {
  product: Product
  isFavorite: boolean
  onToggleFavorite: () => void
  onSelect?: () => void
  shareReferrerId?: string | null
  motionIndex?: number
}) {
  const minPrice = useMemo(() => getMinPrice(product), [product])
  const [copied, setCopied] = useState(false)

  const copyShareLink = async () => {
    try {
      const shareLink = buildProductShareLink(product.id, shareReferrerId)
      if (!shareLink) return
      await navigator.clipboard.writeText(shareLink)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch (error) {
      console.error('Không thể copy link sản phẩm:', error)
    }
  }

  return (
    <motion.div
      {...slideInBottom(motionIndex * 0.05)}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.35, delay: motionIndex * 0.05 }}
      whileHover={{ y: -3 }}
      className="w-full flex flex-col gap-2 rounded-xl border border-slate-200/70 dark:border-white/10 bg-white/75 dark:bg-white/5 transition-colors px-3 py-2 card-hover-lift"
    >
      {/* Top row: Image + Info */}
      <div className="flex items-center gap-3">
        <div className="h-12 w-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-white/10 border border-slate-200/70 dark:border-white/10 flex-shrink-0">
          <img
            src={product.image_url || 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=200'}
            alt={product.name}
            loading="lazy"
            decoding="async"
            width={64}
            height={48}
            className="h-full w-full object-cover"
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-gray-900 dark:text-white truncate" title={product.name}>
            {product.name}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {product.is_hot ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white">
                <Flame className="h-3 w-3" fill="currentColor" />
                HOT
              </span>
            ) : null}
            {minPrice != null ? (
              <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                Từ {formatVnd(minPrice)}
              </span>
            ) : (
              <span className="text-[11px] text-gray-500 dark:text-slate-300">-</span>
            )}
          </div>
        </div>
      </div>

      {/* Bottom row: Action buttons */}
      <div className="flex items-center gap-1.5 justify-end">
        {/* Nút tạo link giới thiệu */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void copyShareLink()
          }}
          className="group relative p-1.5 rounded-full flex items-center justify-center bg-cyan-600 text-white hover:bg-cyan-700 transition-colors"
          aria-label={copied ? 'Đã copy link giới thiệu' : 'Tạo link giới thiệu'}
        >
          {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
          <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity z-50">
            {copied ? 'Đã copy' : 'Tạo link giới thiệu'}
          </span>
        </button>

        {/* Nút yêu thích */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onToggleFavorite()
          }}
          className={
            'group relative p-1.5 rounded-full flex items-center justify-center transition-colors ' +
            (isFavorite
              ? 'bg-rose-600 text-white hover:bg-rose-700'
              : 'bg-white/70 dark:bg-white/5 text-gray-700 dark:text-white border border-slate-200/70 dark:border-white/10 hover:bg-white dark:hover:bg-white/10')
          }
          aria-label={isFavorite ? 'Bỏ yêu thích' : 'Thêm yêu thích'}
        >
          <Heart className="h-4 w-4" fill={isFavorite ? 'currentColor' : 'none'} />
          <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity z-50">
            {isFavorite ? 'Bỏ yêu thích' : 'Thêm yêu thích'}
          </span>
        </button>

        {/* Nút hướng dẫn - chỉ hiện khi có guide_url */}
        {product.guide_url && (
          <a
            href={product.guide_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="group relative p-1.5 rounded-full flex items-center justify-center bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
            aria-label="Xem hướng dẫn"
          >
            <BookOpen className="h-4 w-4" />
            <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity z-50">
              Xem hướng dẫn
            </span>
          </a>
        )}

        {/* Nút xem các gói */}
        {onSelect && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onSelect()
            }}
            className="group relative p-1.5 rounded-full flex items-center justify-center bg-amber-600 text-white hover:bg-amber-700 transition-colors"
            aria-label="Xem các gói"
          >
            <Package className="h-4 w-4" />
            <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity z-50">
              Xem các gói
            </span>
          </button>
        )}

        {/* Nút xem tham khảo - chỉ hiện khi có reference_url */}
        {product.reference_url && (
          <a
            href={product.reference_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="group relative p-1.5 rounded-full flex items-center justify-center bg-purple-600 text-white hover:bg-purple-700 transition-colors"
            aria-label="Xem tham khảo"
          >
            <Link2 className="h-4 w-4" />
            <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity z-50">
              Xem tham khảo
            </span>
          </a>
        )}
      </div>
    </motion.div>
  )
}

export default function ProductsUnifiedPanel({
  products,
  favoriteProductIds,
  favoritesLoading,
  onToggleFavorite,
  onSelectProduct,
  onOpenFavorites,
  onOpenHot,
  shareReferrerId,
}: Props) {
  const [showDesktopBanners, setShowDesktopBanners] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true
    return !window.matchMedia('(max-width: 767px)').matches
  })
  const showFavoritesPanel = showDesktopBanners

  const favoriteProducts = useMemo(() => {
    if (!favoriteProductIds.size) return []
    return products.filter(p => favoriteProductIds.has(p.id))
  }, [products, favoriteProductIds])

  const hotProducts = useMemo(() => {
    return products.filter(p => !!p.is_hot && !p.is_paused)
  }, [products])

  const activeProducts = useMemo(() => {
    return products.filter((p) => !p.is_paused)
  }, [products])

  const bannerProducts = useMemo(() => {
    const source = hotProducts.length
      ? hotProducts
      : products.filter((p) => !p.is_paused)
    return source.slice(0, 8)
  }, [hotProducts, products])

  const [bannerIndex, setBannerIndex] = useState(0)
  const [secondaryBannerIndex, setSecondaryBannerIndex] = useState(0)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsTitle, setDetailsTitle] = useState('')
  const [detailsHtml, setDetailsHtml] = useState('')

  const sanitizedDetailsHtml = useMemo(() => {
    return DOMPurify.sanitize(detailsHtml || '', { USE_PROFILES: { html: true } })
  }, [detailsHtml])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const handleChange = () => setShowDesktopBanners(!mediaQuery.matches)
    handleChange()

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  useEffect(() => {
    setBannerIndex(0)
  }, [bannerProducts.length, showDesktopBanners])

  useEffect(() => {
    if (!showDesktopBanners) return
    if (bannerProducts.length <= 1) return
    const interval = window.setInterval(() => {
      setBannerIndex((prev) => (prev + 1) % bannerProducts.length)
    }, 5000)
    return () => window.clearInterval(interval)
  }, [bannerProducts.length, showDesktopBanners])

  const featuredProduct = bannerProducts[bannerIndex] || null
  const featuredProductId = featuredProduct?.id || null
  const featuredMinPrice = useMemo(
    () => (featuredProduct ? getMinPrice(featuredProduct) : null),
    [featuredProduct]
  )

  const secondaryBannerPool = useMemo(() => {
    if (!activeProducts.length) return []
    if (!featuredProductId) return activeProducts
    const filtered = activeProducts.filter((p) => p.id !== featuredProductId)
    return filtered.length ? filtered : activeProducts
  }, [activeProducts, featuredProductId])

  useEffect(() => {
    setSecondaryBannerIndex(0)
  }, [secondaryBannerPool.length, showDesktopBanners])

  useEffect(() => {
    if (!showDesktopBanners) return
    if (!secondaryBannerPool.length) return
    setSecondaryBannerIndex(Math.floor(Math.random() * secondaryBannerPool.length))
  }, [featuredProductId, secondaryBannerPool.length, showDesktopBanners])

  useEffect(() => {
    if (!showDesktopBanners) return
    if (secondaryBannerPool.length <= 1) return
    const interval = window.setInterval(() => {
      setSecondaryBannerIndex((prev) => pickNextRandomIndex(secondaryBannerPool.length, prev))
    }, 5000)
    return () => window.clearInterval(interval)
  }, [secondaryBannerPool.length, showDesktopBanners])

  const secondaryFeaturedProduct = secondaryBannerPool[secondaryBannerIndex] || null
  const secondaryFeaturedMinPrice = useMemo(
    () => (secondaryFeaturedProduct ? getMinPrice(secondaryFeaturedProduct) : null),
    [secondaryFeaturedProduct]
  )

  const openDescription = (product: Product) => {
    const rawDescription = String(product.description_html || '').trim()
    const descriptionHtml = rawDescription || buildFallbackDescriptionHtml(product)
    setDetailsTitle(`Mô tả: ${product.name}`)
    setDetailsHtml(descriptionHtml)
    setDetailsOpen(true)
  }

  return (
    <>
      <div className="card-soft overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200/70 dark:border-white/10">
          <div className="text-sm font-black text-gray-900 dark:text-white tracking-wide">Tổng quan</div>
          <div className="text-[11px] text-gray-500 dark:text-slate-300 truncate">
            {showDesktopBanners ? 'Banner • Yêu thích • Hot' : 'Hot'}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 xl:h-[clamp(590px,calc(72vh+30px),820px)]">
          {/* Cột trái: 2 Banners xếp dọc */}
          {showDesktopBanners ? (
            <div className="order-1 xl:order-2 p-3 sm:p-4 space-y-3 border-b xl:border-b-0 border-slate-200/70 dark:border-white/10 xl:flex xl:flex-col xl:h-full">
              {featuredProduct ? (
                <div className="relative min-h-[220px] sm:min-h-[260px] xl:min-h-0 xl:flex-1 rounded-2xl overflow-hidden border border-slate-200/70 dark:border-white/10" style={{ willChange: 'transform', contain: 'layout paint' }}>
                  <img
                    src={featuredProduct.image_url || 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=800'}
                    alt={featuredProduct.name}
                    loading="eager"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/20" />

                  <div className="absolute top-3 right-3 flex items-center gap-1.5">
                    {bannerProducts.map((product, index) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => setBannerIndex(index)}
                        className={`h-2.5 rounded-full transition-all ${index === bannerIndex ? 'w-6 bg-white' : 'w-2.5 bg-white/50 hover:bg-white/80'}`}
                        aria-label={`Mở banner ${index + 1}`}
                      />
                    ))}
                  </div>

                  <div className="relative z-10 h-full flex flex-col justify-end p-4 sm:p-6">
                    <div className="inline-flex w-fit items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-red-600 text-white mb-2">
                      <Flame className="h-3 w-3" fill="currentColor" />
                      SẢN PHẨM NỔI BẬT
                    </div>

                    <div className="text-2xl sm:text-3xl font-black text-white leading-tight line-clamp-2">
                      {featuredProduct.name}
                    </div>

                    <div className="mt-1.5 text-sm text-white/90 line-clamp-2">
                      {(featuredProduct.mechanism || featuredProduct.recommended_model || 'Giá tốt, giao nhanh, hỗ trợ tận tâm.')}
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="text-xs text-white/80">
                        {featuredMinPrice != null ? (
                          <span>Từ <span className="font-extrabold text-amber-300">{formatVnd(featuredMinPrice)}</span></span>
                        ) : (
                          <span>Đang cập nhật giá</span>
                        )}
                      </div>
                      {featuredProduct.product_code ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/40 text-white/90 font-semibold">
                          {featuredProduct.product_code}
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => onSelectProduct?.(featuredProduct.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold shadow-sm"
                      >
                        <Package className="h-4 w-4" />
                        Xem gói
                      </button>
                      <button
                        type="button"
                        onClick={() => openDescription(featuredProduct)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-bold border border-white/30"
                      >
                        <FileText className="h-4 w-4" />
                        Xem mô tả
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="min-h-[220px] sm:min-h-[260px] xl:min-h-0 xl:flex-1 rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/60 dark:bg-white/5 flex items-center justify-center text-sm text-gray-500 dark:text-slate-300 italic">
                  Chưa có sản phẩm để hiển thị banner.
                </div>
              )}

              {secondaryFeaturedProduct ? (
                <div className="relative min-h-[220px] sm:min-h-[260px] xl:min-h-0 xl:flex-1 rounded-2xl overflow-hidden border border-slate-200/70 dark:border-white/10" style={{ willChange: 'transform', contain: 'layout paint' }}>
                  <img
                    src={secondaryFeaturedProduct.image_url || 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=800'}
                    alt={secondaryFeaturedProduct.name}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/20" />

                  <div className="absolute top-3 right-3 rounded-full px-2 py-1 bg-white/15 border border-white/25 text-[10px] font-semibold text-white">
                    Ngẫu nhiên 5s
                  </div>

                  <div className="relative z-10 h-full flex flex-col justify-end p-4 sm:p-6">
                    <div className="inline-flex w-fit items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-600 text-white mb-2">
                      <Package className="h-3 w-3" />
                      SẢN PHẨM ĐANG BÁN
                    </div>

                    <div className="text-xl sm:text-2xl font-black text-white leading-tight line-clamp-2">
                      {secondaryFeaturedProduct.name}
                    </div>

                    <div className="mt-1.5 text-sm text-white/90 line-clamp-2">
                      {(secondaryFeaturedProduct.mechanism || secondaryFeaturedProduct.recommended_model || 'Sản phẩm đang được mở bán, có thể mua ngay.')}
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="text-xs text-white/80">
                        {secondaryFeaturedMinPrice != null ? (
                          <span>Từ <span className="font-extrabold text-amber-300">{formatVnd(secondaryFeaturedMinPrice)}</span></span>
                        ) : (
                          <span>Đang cập nhật giá</span>
                        )}
                      </div>
                      {secondaryFeaturedProduct.product_code ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/40 text-white/90 font-semibold">
                          {secondaryFeaturedProduct.product_code}
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => onSelectProduct?.(secondaryFeaturedProduct.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold shadow-sm"
                      >
                        <Package className="h-4 w-4" />
                        Xem gói
                      </button>
                      <button
                        type="button"
                        onClick={() => openDescription(secondaryFeaturedProduct)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-bold border border-white/30"
                      >
                        <FileText className="h-4 w-4" />
                        Xem mô tả
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="min-h-[220px] sm:min-h-[260px] xl:min-h-0 xl:flex-1 rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/60 dark:bg-white/5 flex items-center justify-center text-sm text-gray-500 dark:text-slate-300 italic">
                  Chưa có sản phẩm đang bán để hiển thị ngẫu nhiên.
                </div>
              )}
            </div>
          ) : null}

          {/* Cột phải: Yêu thích (desktop) + Hot */}
          <div
            className={
              'order-2 xl:order-1 flex flex-col xl:h-full xl:border-r border-slate-200/70 dark:border-white/10 '
              + (showFavoritesPanel ? 'divide-y divide-slate-200/70 dark:divide-white/10' : '')
            }
          >
            {/* Nửa trên: Yêu thích (desktop) */}
            {showFavoritesPanel ? (
              <div className="flex-1 min-h-0 max-h-[320px] xl:max-h-none p-3 sm:p-4 overflow-y-auto custom-scrollbar">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Heart className="h-3 w-3 text-rose-500" fill="currentColor" />
                    <div className="text-xs font-black text-gray-700 dark:text-slate-200 uppercase tracking-wider">
                      Yêu thích {favoriteProductIds.size > 0 && <span className="ml-1 opacity-60">({favoriteProductIds.size})</span>}
                    </div>
                    {onOpenFavorites && favoriteProductIds.size > 0 ? (
                      <button
                        type="button"
                        onClick={onOpenFavorites}
                        className="ml-auto text-[10px] text-amber-600 hover:underline dark:text-amber-300"
                      >
                        Mở danh sách
                      </button>
                    ) : null}
                  </div>

                  {favoritesLoading ? (
                    <div className="text-sm text-gray-500 dark:text-slate-300">Đang tải sản phẩm yêu thích...</div>
                  ) : favoriteProducts.length ? (
                    <div className="space-y-2">
                      {favoriteProducts.slice(0, 6).map((p, index) => (
                        <ProductQuickItem
                          key={p.id}
                          product={p}
                          isFavorite={favoriteProductIds.has(p.id)}
                          onToggleFavorite={() => onToggleFavorite(p.id)}
                          onSelect={onSelectProduct ? () => onSelectProduct(p.id) : undefined}
                          shareReferrerId={shareReferrerId}
                          motionIndex={index}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-slate-300 italic py-2 px-1">Chưa có sản phẩm yêu thích.</div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Nửa dưới: Sản phẩm Hot */}
            <div className="flex-1 min-h-0 max-h-[320px] xl:max-h-none p-3 sm:p-4 overflow-y-auto custom-scrollbar">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Flame className="h-3 w-3 text-red-500" fill="currentColor" />
                  <div className="text-xs font-black text-gray-700 dark:text-slate-200 uppercase tracking-wider">
                    Sản phẩm Hot {hotProducts.length > 0 && <span className="ml-1 opacity-60">({hotProducts.length})</span>}
                  </div>
                  {onOpenHot && hotProducts.length > 0 ? (
                    <button
                      type="button"
                      onClick={onOpenHot}
                      className="ml-auto text-[10px] text-amber-600 hover:underline dark:text-amber-300"
                    >
                      Xem nhanh
                    </button>
                  ) : null}
                </div>

                {hotProducts.length ? (
                  <div className="space-y-2">
                    {hotProducts.slice(0, 6).map((p, index) => (
                      <ProductQuickItem
                        key={p.id}
                        product={p}
                        isFavorite={favoriteProductIds.has(p.id)}
                        onToggleFavorite={() => onToggleFavorite(p.id)}
                        onSelect={onSelectProduct ? () => onSelectProduct(p.id) : undefined}
                        shareReferrerId={shareReferrerId}
                        motionIndex={showFavoritesPanel ? index + 6 : index}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-slate-300 italic py-2 px-1">Chưa có sản phẩm hot.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {detailsOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center p-4 pb-[env(safe-area-inset-bottom,0.5rem)] overflow-y-auto z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-3xl my-auto max-h-[85vh] sm:max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-800 shadow-lg">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="text-base font-semibold text-gray-900 dark:text-gray-100">{detailsTitle}</div>
                <button
                  type="button"
                  onClick={() => setDetailsOpen(false)}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm font-semibold"
                >
                  <X className="h-4 w-4" />
                  Đóng
                </button>
              </div>

              <div
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: sanitizedDetailsHtml }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
